import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useNavigate } from 'react-router-dom'

import { useCreatePromotion } from '@/lib/api/promotions'
import { toPromotionDayString } from '@/lib/promotion-date'
import { PromotionForm, PromotionFormData } from '@/components/form/discount/promotion-form'
import { PromotionSummary } from '@/components/form/discount/promotion-summary'
import { PromotionFloatingActionBar } from '@/components/form/discount/promotion-floating-action-bar'
import { useStoreNavigate } from '@/hooks/use-app-navigate'

const CreatePromotionPageContainerInner = () => {
    const { storeRoutes, navigate, id: storeId } = useStoreNavigate()
    const rawNavigate = useNavigate()
    const createPromotion = useCreatePromotion()

    const handleSubmit = async (data: PromotionFormData, { reset }: { reset: (values?: PromotionFormData) => void }) => {
        if (!storeId) return

        const createData = {
            storeId,
            name: data.name,
            enabled: data.enabled ?? false,
            automatic: data.automatic ?? false,
            usageLimit: data.usageLimit || undefined,
            startDate: data.startDate ? toPromotionDayString(data.startDate) : undefined,
            endDate: data.endDate ? toPromotionDayString(data.endDate) : undefined,
            ruleOperator: data.ruleOperator ?? 'AND',
            combinesWithOrderLevelDiscounts: data.combinesWithOrderLevelDiscounts ?? false,
            combinesWithLineItemDiscounts: data.combinesWithLineItemDiscounts ?? false,
            combinesWithShippingDiscounts: data.combinesWithShippingDiscounts ?? false,
            forceCombine: data.forceCombine ?? false,
            oncePerCustomer: data.oncePerCustomer ?? false,
            rules: data.rules.map((rule) => ({
                type: rule.type,
                productId: rule.productId,
                minimumQuantity: rule.minimumQuantity,
                minimumAmount: rule.minimumAmount,
                maximumAmount: rule.maximumAmount,
                variantIds: rule.variantIds,
                orderMinQuantity: rule.orderMinQuantity,
                orderMaxQuantity: rule.orderMaxQuantity,
                excludedProductIds: rule.excludedProductIds,
                maximumQuantity: rule.maximumQuantity,
                paymentMethodName: rule.paymentMethodName,
            })),
            actions: data.actions.map((action) => ({
                type: action.type,
                adjustmentType: action.adjustmentType,
                adjustmentPercentage: action.adjustmentPercentage,
                adjustmentAmount: action.adjustmentAmount,
                freeShipping: action.freeShipping,
                maxShippingAmount: action.maxShippingAmount,
                productIdToAdd: action.productIdToAdd,
                targetProductId: action.targetProductId,
                targetVariantIds: action.targetVariantIds,
                maxQuantityDiscounted: action.maxQuantityDiscounted,
                appliesOnEachItem: action.appliesOnEachItem,
                subscriptionFreeTrialDuration: action.subscriptionFreeTrialDuration,
                subscriptionFreeTrialDurationType: action.subscriptionFreeTrialDurationType,
                priceIdToAdd: action.priceIdToAdd,
                variantIdToAdd: action.variantIdToAdd,
                quantityToAdd: action.quantityToAdd,
                buyQuantityThreshold: action.buyQuantityThreshold,
            })),
        }
        await createPromotion.mutateAsync(createData as any, {
            onSuccess: () => {
                reset(data)
                rawNavigate(storeRoutes.DISCOUNTS())
            },
        })
    }

    const handleCancel = () => {
        if (!storeId) return
        navigate(storeRoutes.DISCOUNTS())
    }

    if (!storeId) {
        return null
    }

    return (
        <PromotionForm
            Aside={() => <PromotionSummary />}
            BottomBar={(bar) => <PromotionFloatingActionBar mode="create" {...bar} />}
            mode="create"
            storeId={storeId}
            isPending={createPromotion.isPending}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
        />
    )
}

export const CreatePromotionPageContainer = withRequiredPermission(CreatePromotionPageContainerInner, PERMISSIONS.STORES_MANAGE)
